import { Link } from 'react-router-dom';
import { PlayCircle, UserPlus, Users, Compass, Presentation, LogIn, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function HomePage() {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-mp-bg-lightgreen flex flex-col font-body">
      <header className="flex items-center justify-between px-6 pt-5 pb-2 max-w-[480px] mx-auto w-full">
        <span className="mp-eyebrow">Mikkelparken</span>
        <div>
          {user ? (
            <button
              onClick={logout}
              className="text-mp-stone-500 hover:text-mp-darkgreen transition-colors"
              title={`Logga inn som ${user.email}`}
            >
              <LogOut size={20} />
            </button>
          ) : (
            <Link
              to="/login"
              className="text-mp-stone-500 hover:text-mp-darkgreen transition-colors"
              title="Logg inn"
            >
              <LogIn size={20} />
            </Link>
          )}
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center pb-12 max-w-[480px] mx-auto w-full">
        <div className="flex justify-center pt-3 pb-3">
          <img src="/Mikkel tenker uten bakgrunn.png" alt="Mikkel" className="w-[180px] h-auto" />
        </div>

        <h1 className="font-display text-mp-darkgreen text-[44px] text-center mb-2 tracking-tight">
          Mikkel kviss
        </h1>
        <p className="font-body text-mp-stone-700 text-[15px] text-center mx-7 mb-7">
          Tre måtar å spele saman på — for sommarvenene, gjestane og venene dine.
        </p>

        {/* Quiz */}
        <article className="mx-[18px] mb-3.5 p-[22px] pb-6 rounded-[20px] bg-mp-bg-lightgreen border border-[rgba(17,73,66,0.12)] shadow-mp-1 w-[calc(100%-36px)]">
          <div className="flex justify-between items-center mb-3.5">
            <span className="mp-eyebrow">Quiz · klassisk</span>
            <span className="inline-flex items-center bg-mp-bg-mint text-mp-darkgreen font-display font-bold text-[13px] px-3.5 py-1.5 rounded-full">
              A B C D
            </span>
          </div>
          <h2 className="font-display text-[24px] text-mp-darkgreen mb-1">Mikkel kviss</h2>
          <p className="font-body text-[14px] text-mp-stone-700 mb-5 leading-[1.5]">
            Lag spørsmål, kall inn deltakarar med PIN, og spel saman på storskjerm.
          </p>
          <div className="flex gap-3.5 items-center flex-wrap">
            <Link to="/host" className="mp-cta">
              <PlayCircle size={20} />
              <span>Start ein kviss</span>
            </Link>
            <Link
              to="/join"
              className="text-mp-magenta font-display font-bold text-[15px] no-underline hover:underline"
            >
              Bli med
            </Link>
          </div>
        </article>

        {/* Mikkelchase */}
        <article className="mx-[18px] mb-3.5 p-[22px] pb-6 rounded-[20px] bg-mp-bg-pink border border-[rgba(17,73,66,0.12)] shadow-mp-1 w-[calc(100%-36px)]">
          <div className="flex justify-between items-center mb-3.5">
            <span className="mp-eyebrow">Mikkelchase · ute</span>
            <span className="inline-flex items-center bg-white/60 text-mp-darkgreen font-display font-bold text-[13px] px-3.5 py-1.5 rounded-full border border-[rgba(17,73,66,0.12)]">
              <Users size={14} className="mr-1.5" /> Lag · Foto
            </span>
          </div>
          <h2 className="font-display text-[24px] text-mp-darkgreen mb-1">Mikkelchase</h2>
          <p className="font-body text-[14px] text-mp-stone-700 mb-5 leading-[1.5]">
            Skattejakt rundt parken. Lag besøker stasjonar, løyser oppgåver og sender foto.
          </p>
          <div className="flex gap-3.5 items-center flex-wrap">
            <Link to="/mikkelchase" className="mp-cta">
              <Compass size={20} />
              <span>Start ein chase</span>
            </Link>
            <Link
              to="/mikkelchase/join"
              className="text-mp-magenta font-display font-bold text-[15px] no-underline hover:underline"
            >
              Bli med som lag
            </Link>
          </div>
        </article>

        {/* Menti */}
        <article className="mx-[18px] mb-3.5 p-[22px] pb-6 rounded-[20px] bg-mp-bg-blue border border-[rgba(17,73,66,0.12)] shadow-mp-1 w-[calc(100%-36px)]">
          <div className="flex justify-between items-center mb-3.5">
            <span className="mp-eyebrow">Menti · respons</span>
            <span className="inline-flex items-center bg-white/60 text-mp-teal font-display font-bold text-[13px] px-3.5 py-1.5 rounded-full border border-[rgba(0,137,134,0.18)]">
              Live
            </span>
          </div>
          <h2 className="font-display text-[24px] text-mp-darkgreen mb-1">Mikkel menti</h2>
          <p className="font-body text-[14px] text-mp-stone-700 mb-5 leading-[1.5]">
            Ord-skyer, opne svar og avstemmingar — for alt frå opplæring til familieråd.
          </p>
          <div className="flex gap-3.5 items-center flex-wrap">
            <Link to="/menti" className="mp-cta">
              <Presentation size={20} />
              <span>Start ein menti</span>
            </Link>
            <Link
              to="/menti/join"
              className="text-mp-magenta font-display font-bold text-[15px] no-underline hover:underline"
            >
              <UserPlus size={16} className="inline mr-1" /> Bli med
            </Link>
          </div>
        </article>
      </main>

      <footer className="p-6 text-center">
        <p className="text-mp-stone-500 text-[12px] font-body">
          © Mikkelparken · «Her blir gode minner skapt»
        </p>
        <p className="text-mp-stone-500 text-[11px] mt-1">
          Versjon {__APP_VERSION__} ·{' '}
          {new Date(__BUILD_TIME__).toLocaleString('nn-NO', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </footer>
    </div>
  );
}
